import { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTour } from './TourProvider';

/**
 * TourContinuityManager - A component that maintains tour continuity across page changes
 * Place this component at the top level of your application to ensure tours persist
 * across route changes.
 */
const TourContinuityManager = () => {
  const { activeTour } = useSelector((state: any) => state.tour);
  const { isTourActive, startTour } = useTour();
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  // Use refs to prevent excessive operations
  const isProcessingNavigationRef = useRef(false);
  const lastNavigationTimeRef = useRef(0);
  const observerRef = useRef<MutationObserver | null>(null);
  const navigationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Throttled path change handler to prevent excessive operations
  const handleLocationChange = useCallback(() => {
    const now = Date.now();
    
    // Rate limit navigation handling to prevent excessive calls
    if (now - lastNavigationTimeRef.current < 1000) { // 1 second throttle
      return;
    }

    const newPath = window.location.pathname;
    if (newPath !== currentPath && !isProcessingNavigationRef.current) {
      isProcessingNavigationRef.current = true;
      lastNavigationTimeRef.current = now;
      
      // Clear any existing timeout
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }

      // Debounce the path update to prevent rapid successive updates
      navigationTimeoutRef.current = setTimeout(() => {
        setCurrentPath(newPath);
        isProcessingNavigationRef.current = false;
      }, 300);
    }
  }, [currentPath]);

  // Optimized DOM change handler with better throttling
  const handleDOMChange = useCallback(() => {
    // Only check for navigation if we're not already processing
    if (!isProcessingNavigationRef.current) {
      handleLocationChange();
    }
  }, [handleLocationChange]);

  // Use browser history API to detect path changes
  useEffect(() => {
    // Add event listeners for history API
    window.addEventListener('popstate', handleLocationChange);

    // Create a more efficient MutationObserver with throttling
    const createOptimizedObserver = () => {
      let observerTimeout: NodeJS.Timeout | null = null;
      
      return new MutationObserver((mutations) => {
        // Only process if there are actual navigation-related changes
        const hasRelevantChanges = mutations.some(mutation => {
          // Look for changes that might indicate navigation
          return mutation.type === 'childList' && 
                 mutation.target.nodeName === 'BODY' &&
                 mutation.addedNodes.length > 0;
        });

        if (hasRelevantChanges && !observerTimeout) {
          // Throttle observer callbacks to prevent excessive processing
          observerTimeout = setTimeout(() => {
            handleDOMChange();
            observerTimeout = null;
          }, 500);
        }
      });
    };

    // Create and start the optimized observer
    observerRef.current = createOptimizedObserver();
    
    // Start observing with more specific configuration
    observerRef.current.observe(document.body, { 
      childList: true, 
      subtree: false, // Only watch direct children of body to reduce overhead
      attributes: false, // Don't watch attribute changes
      characterData: false // Don't watch text changes
    });

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
        navigationTimeoutRef.current = null;
      }
    };
  }, [handleLocationChange, handleDOMChange]);

  // Effect to handle page changes while a tour is active - with proper safeguards
  useEffect(() => {
    // Only proceed if there's an active tour and it's not currently running
    if (!activeTour || isTourActive(activeTour) || isProcessingNavigationRef.current) {
      return;
    }

    // Add a longer delay to ensure DOM elements have loaded and prevent conflicts
    const timeoutId = setTimeout(() => {
      // Double-check conditions before starting tour
      if (activeTour && !isTourActive(activeTour) && !isProcessingNavigationRef.current) {
        try {
          startTour(activeTour);
        } catch (error) {
          console.error('Error restarting tour after navigation:', error);
        }
      }
    }, 800); // Increased delay to prevent conflicts

    return () => clearTimeout(timeoutId);
  }, [currentPath, activeTour, isTourActive, startTour]);

  return null; // This is a utility component with no UI
};

export default TourContinuityManager; 